
import React, { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';

interface ChatCodeBlockProps {
  language?: string;
  value: string;
  className?: string;
}

export const ChatCodeBlock: React.FC<ChatCodeBlockProps> = ({ 
  language, 
  value,
  className 
}) => {
  const [copied, setCopied] = useState(false);

  const onCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Simple formatting for JSON content to make it more readable
  const formattedValue = React.useMemo(() => {
    if (language?.toLowerCase() === 'json') {
      try {
        const parsedJson = JSON.parse(value);
        return JSON.stringify(parsedJson, null, 2);
      } catch {
        // If parsing fails, return the original value
        return value;
      }
    }
    return value;
  }, [language, value]);

  return (
    <motion.div 
      className={cn(
        "relative my-2 rounded-md overflow-hidden bg-background/80 border border-white/10",
        className
      )}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {language && (
        <div className="flex items-center justify-between px-3 py-1.5 border-b border-white/10 bg-background/50 text-xs text-muted-foreground">
          <span>{language}</span>
          <button
            onClick={onCopy}
            className="p-1 hover:bg-muted rounded-md transition-colors"
            aria-label={copied ? "Copied to clipboard" : "Copy to clipboard"}
          >
            {copied ? (
              <Check className="h-3.5 w-3.5 text-green-500" />
            ) : (
              <Copy className="h-3.5 w-3.5" />
            )}
          </button>
        </div>
      )}
      <pre className="p-3 text-xs overflow-auto whitespace-pre max-h-[400px] scrollbar-thin scrollbar-thumb-gray-400 scrollbar-track-transparent">
        <code>{formattedValue}</code>
      </pre>
    </motion.div>
  );
};
